import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { cn } from '@/lib/utils';

// Message loading skeleton
export function MessageSkeleton() {
  return (
    <div className="flex gap-3 max-w-4xl mx-auto px-4 py-3">
      {/* Avatar */}
      <Skeleton className="flex-shrink-0 w-8 h-8 rounded-full" />
      
      {/* Message Content */}
      <div className="flex-1 min-w-0">
        {/* Message Header */}
        <div className="flex items-center gap-2 mb-1">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-3 w-12" />
        </div>
        
        {/* Message Bubble */}
        <Card className="max-w-[80%]">
          <CardContent className="p-3">
            <div className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Chat loading skeleton (multiple messages)
export function ChatSkeleton() {
  return (
    <div className="py-4 space-y-1">
      <MessageSkeleton />
      <MessageSkeleton />
      <MessageSkeleton />
    </div>
  );
}

// Session list loading skeleton
export function SessionListSkeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <Card key={i} className="p-3">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-3 w-16" />
            </div>
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-2/3" />
          </div>
        </Card>
      ))}
    </div>
  );
}

// Sidebar loading skeleton
export function SidebarSkeleton() {
  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="space-y-2">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-9 w-full" />
      </div>
      
      {/* Search */}
      <Skeleton className="h-9 w-full" />
      
      {/* Session list */}
      <SessionListSkeleton />
    </div>
  );
}

// Artifact loading skeleton
export function ArtifactSkeleton({ type = 'chart' }: { type?: 'chart' | 'table' | 'image' | 'json' }) {
  return (
    <Card className="mt-4">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <Skeleton className="h-5 w-24" />
          <Skeleton className="h-4 w-16" />
        </div>
      </CardHeader>
      <CardContent>
        {type === 'chart' && (
          <Skeleton className="h-64 w-full" />
        )}
        {type === 'table' && (
          <div className="space-y-2">
            <div className="flex gap-2">
              <Skeleton className="h-8 flex-1" />
              <Skeleton className="h-8 flex-1" />
              <Skeleton className="h-8 flex-1" />
            </div>
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex gap-2">
                <Skeleton className="h-6 flex-1" />
                <Skeleton className="h-6 flex-1" />
                <Skeleton className="h-6 flex-1" />
              </div>
            ))}
          </div>
        )}
        {type === 'image' && (
          <Skeleton className="h-48 w-full" />
        )}
        {type === 'json' && (
          <div className="space-y-1">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className={`h-4 ${i % 2 === 0 ? 'w-full' : 'w-3/4 ml-4'}`} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Page loading skeleton
export function PageSkeleton() {
  return (
    <div className="flex h-screen">
      {/* Sidebar skeleton */}
      <div className="w-80 border-r bg-muted/10">
        <SidebarSkeleton />
      </div>
      
      {/* Main content skeleton */}
      <div className="flex-1 flex flex-col">
        {/* Header skeleton */}
        <div className="border-b p-4">
          <div className="flex items-center justify-between">
            <Skeleton className="h-6 w-48" />
            <div className="flex gap-2">
              <Skeleton className="h-8 w-8 rounded-full" />
              <Skeleton className="h-8 w-8 rounded-full" />
            </div>
          </div>
        </div>
        
        {/* Chat skeleton */}
        <div className="flex-1 overflow-hidden">
          <ChatSkeleton />
        </div>
        
        {/* Input skeleton */}
        <div className="border-t p-4">
          <Skeleton className="h-10 w-full" />
        </div>
      </div>
    </div>
  );
}

// Generic loading spinner
export function LoadingSpinner({ size = 'default' }: { size?: 'sm' | 'default' | 'lg' }) {
  const sizeClasses = {
    sm: 'h-4 w-4',
    default: 'h-6 w-6',
    lg: 'h-8 w-8'
  };

  return (
    <div className="flex items-center justify-center">
      <div className={`${sizeClasses[size]} animate-spin rounded-full border-2 border-muted border-t-primary`} />
    </div>
  );
}

// Corpus loading skeleton
export function CorpusSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div className="space-y-2 p-2">
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i}>
          <CardContent className="p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Skeleton className="h-3 w-3 rounded" />
                  <Skeleton className="h-4 flex-1" />
                </div>
                <div className="flex items-center gap-3">
                  <Skeleton className="h-3 w-16" />
                </div>
              </div>
              <Skeleton className="h-3 w-3 rounded" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// Loading overlay
export function LoadingOverlay({ 
  message = 'Loading...', 
  className,
  variant = 'default' 
}: { 
  message?: string;
  className?: string;
  variant?: 'default' | 'minimal' | 'fullscreen';
}) {
  if (variant === 'minimal') {
    return (
      <div className={cn('flex items-center justify-center p-4', className)}>
        <div className="flex items-center gap-2">
          <LoadingSpinner size="default" />
          <p className="text-sm text-muted-foreground">{message}</p>
        </div>
      </div>
    );
  }

  if (variant === 'fullscreen') {
    return (
      <div className="fixed inset-0 bg-background/90 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="flex flex-col items-center gap-4 p-8 rounded-lg bg-card border shadow-lg">
          <LoadingSpinner size="lg" />
          <div className="text-center">
            <p className="text-lg font-medium">{message}</p>
            <p className="text-sm text-muted-foreground mt-1">Please wait...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('absolute inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50', className)}>
      <div className="flex flex-col items-center gap-2">
        <LoadingSpinner size="lg" />
        <p className="text-sm text-muted-foreground">{message}</p>
      </div>
    </div>
  );
}